import styles from './customerForm.module.css'
import { Labels, validateIdentifier, validateTaxId, EmailFormModal, EmailsTable, PhoneFormModal, PhonesTable, Data2, DataTypeForm2, useDescribeHooks, ArchiveModal } from "../../../../../features/data-types"
import { PanelWrapper, Data, FieldType, FieldValue, FormField, NumberField, option, useGetCrumbs, O, useUIActions, ModalId, Button } from "../../../../../features/ui"
import { useActions } from "./actions"
import { Customer, CustomerCategory } from "../../../state/types"
import { DataType, TableName } from "../../../../../tables"
import { IdentifierType } from '../../../../system'
import { useMemo, useState } from 'react'
import { toNumber } from '../../../../../utils/typeUtils'
import { useLocation, useNavigate } from 'react-router-dom'
import { RoutePath, useRoutesHooks } from '../../../../../features/routes'
import { VatCondition } from '../../../../administration'
import { AddressesTable, AddressFormModal, addressHelpers } from '../../../../../features/address'
import { ContactFormModal } from '../contact-form-modal/contactFormModal'
import { useCustomerHooks } from '../../../hooks/customerHooks'
import { IdentifierField } from '../identifier-field/identifierField'
import { CustomerWarningModal } from '../customer-warning-modal/customerWarningModal'
import { useCustomerTypeLoaders } from '../../../loaders/customerTypeLoaders'

export const CustomerForm = () => {
    const { dataType } = useRoutesHooks().parseUrl()
    const customer = dataType as O<Customer>
    const createMode = !customer

    const emailToData = (email: string) => ({ email })
    const phoneToData = (phone: number) => ({ phoneStr: phone.toString(), phone })
    const contactToData = (contactName?: string, contactPhone?: number) => contactName || contactPhone ? ({ contactName, contactPhone }) : undefined

    const [identifierType, setIdentifierType] = useState<O<IdentifierType>>(customer?.identifierType || IdentifierType.PERSONAL_ID)
    const [identifier, setIdentifier] = useState<O<number>>(customer?.identifier)
    const [identifierValidation, setIdentifierValidation] = useState<string>()
    const [creditAccount, setCreditAccount] = useState<boolean>(!!customer?.creditAccount)
    const [creditLimit, setCreditLimit] = useState<O<number>>(customer?.creditAccount?.creditLimit)
    const [creditDays, setCreditDays] = useState<O<number>>(customer?.creditAccount?.creditDays)
    const [mainAddress, setMainAddress] = useState<O<Data2>>(customer?.mainAddress)
    const [emailsData, setEmailsData] = useState<Data2[]>(customer?.emails.map(emailToData) || [])
    const [currentEmail, setCurrentEmail] = useState<string>()
    const [phonesData, setPhonesData] = useState<Data2[]>(customer?.phones.map(phoneToData) || [])
    const [currentPhone, setCurrentPhone] = useState<number>()
    const [deliveryAddressesData, setDeliveryAddressesData] = useState<Data2[]>(customer?.deliveryAddresses || [])
    const [currentDeliveryAddress, setCurrentDeliveryAddress] = useState<Data2>()
    const [contact, setContact] = useState<O<Data>>(contactToData(customer?.contactName, customer?.contactPhone))
    const [isMainAddress, setIsMainAddress] = useState<boolean>(true)
    const [modalData, setModalData] = useState<O<Data2>>()

    const { describeAddress } = addressHelpers
    const { describeContact } = useCustomerHooks()
    const { state } = useLocation()
    const { describe } = useDescribeHooks()
    const navigate = useNavigate()

    const customerTypeLoaders = useCustomerTypeLoaders()

    const { removeAddress, archiveAddress, submitCustomerOrOpenModal, submitCustomer } = useActions()
    const { openModal } = useUIActions()

    const customerLabel = customer ? describe(TableName.CUSTOMERS, customer) : ''
    const title = `${createMode ? 'Crear' : 'Editar'} ${customerLabel || 'Cliente'}`
    const isSale = state?.from?.toString().includes(RoutePath.SALES)
    const crumbs = state?.from ?
        useGetCrumbs(
            isSale ? 'Ventas' : 'Acopios',
            isSale ? RoutePath.SALES : RoutePath.HOARDS,
            state.createMode ? 'Crear' : 'Editar',
            undefined,
            'Crear Cliente'
        ) :
        useGetCrumbs(
            'Clientes',
            RoutePath.CUSTOMERS,
            customer?.name,
            customer?.id,
            createMode ? 'Crear' : 'Editar'
        )

    const customerTypeOptions = useMemo(() => {
        const customerTypes = customerTypeLoaders().findAllCustomerTypes(createMode)
            .filter(customerType => !customerType.archived || customerType.id === customer?.customerTypeId)
        return customerTypes.map(customerType => option(customerType.id, customerType.name))
    }, [])

    const onChangeIdentifierType = (value?: FieldValue) => {
        setIdentifierType(value as O<IdentifierType>)
        setIdentifierValidation(undefined)
    }

    const onChangeIdentifier = (value?: FieldValue) => {
        setIdentifier(value as O<number>)
        setIdentifierValidation(undefined)
    }

    const onChangeCreditAccount = (value?: FieldValue) => {
        const newCreditAccount = !!value
        setCreditAccount(newCreditAccount)
        if (newCreditAccount) {
            setCreditDays(30)
        } else {
            setCreditLimit(undefined)
            setCreditDays(undefined)
        }
    }
    
    const onChangeCreditLimit = (value?: FieldValue) => setCreditLimit(value as O<number>)
    
    const onChangeCreditDays = (value?: FieldValue) => setCreditDays(value as O<number>)
    
    const openEmailModal = () => openModal(ModalId.EMAIL_FORM)
    const openPhoneModal = () => openModal(ModalId.PHONE_FORM)
    const openAddressModal = (mainAddress = false) => {
        setIsMainAddress(mainAddress)
        openModal(ModalId.ADDRESS_FORM)
    }
    const openContactModal = () => openModal(ModalId.CONTACT_FORM)

    const onEditMainAddress = () => openAddressModal(true)

    const onAddEmail = () => {
        setCurrentEmail(undefined)
        openEmailModal()
    }
    
    const onEditEmail = (email: string) => {
        setCurrentEmail(email)
        openEmailModal()
    }
    
    const onRemoveEmail = (email: string) => {
        const newEmailsData = emailsData.filter(emailData => emailData.email !== email)
        setEmailsData(newEmailsData)
    }

    const onAddPhone = () => {
        setCurrentPhone(undefined)
        openPhoneModal()
    }
    
    const onEditPhone = (phoneStr: string) => {
        setCurrentPhone(toNumber(phoneStr))
        openPhoneModal()
    }
    
    const onRemovePhone = (phoneStr: string) => {
        const newPhonesData = phonesData.filter(phoneData => phoneData.phoneStr !== phoneStr)
        setPhonesData(newPhonesData)
    }

    const onAddDeliveryAddress = () => {
        setCurrentDeliveryAddress(undefined)
        openAddressModal()
    }
    
    const onEditDeliveryAddress = (addressId: string) => {
        setCurrentDeliveryAddress(deliveryAddressesData.find(deliveryAddressData => deliveryAddressData.id === addressId))
        openAddressModal()
    }
    
    const onRemoveDeliveryAddress = async (addressId: string) => {
        setCurrentDeliveryAddress(deliveryAddressesData.find(deliveryAddressData => deliveryAddressData.id === addressId))
        const newDeliveryAddressesData = await removeAddress(addressId, deliveryAddressesData)
        newDeliveryAddressesData !== undefined && setDeliveryAddressesData(newDeliveryAddressesData!)
    }

    const onArchiveDeliveryAddress = async () => {
        if (currentDeliveryAddress?.id) {
            const newDeliveryAddressesData = await archiveAddress(currentDeliveryAddress.id as string, deliveryAddressesData)
            newDeliveryAddressesData !== undefined && setDeliveryAddressesData(newDeliveryAddressesData!)
        }
    }

    const fields: FormField[] = [
        {
            name: 'name',
            type: FieldType.TEXT,
            label: 'Nombre / Razón Social'
        },
        {
            name: 'category',
            type: FieldType.SELECT,
            label: 'Cateogría',
            defaultValue: customer?.category || CustomerCategory.NATURAL,
            options: Object.values(CustomerCategory).map(category => option(category, Labels.customerCategoryLabel(category)))
        },
        IdentifierField({
            defaultIdentifierType: identifierType,
            defaultIdentifier: identifier,
            validation: identifierValidation,
            onChangeIdentifierType,
            onChangeIdentifier
        }),
        {
            name: 'customerTypeId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMER_TYPES,
            label: 'Lista de Precios',
            defaultValue: customer?.customerTypeId,
            options: customerTypeOptions
        },
        {
            name: 'vatCondition',
            type: FieldType.SELECT,
            label: 'Condición frente al IVA',
            defaultValue: customer?.vatCondition || VatCondition.FINAL_CONSUMER,
            options: Object.values(VatCondition).map(condition => option(condition, Labels.vatConditionLabel(condition)))
        },
        {
            name: 'hasCreditAccount',
            type: FieldType.BOOLEAN,
            label: 'Cuenta Corriente',
            defaultValue: creditAccount,
            onChange: onChangeCreditAccount
        },
        {
            name: 'mainAddress',
            type: FieldType.TEXT,
            label: 'Domicilio',
            render: () => {
                return (
                    <div className={styles.editableField}>
                        {mainAddress && (
                            <p>{describeAddress(mainAddress)}</p>
                        )}
                        <Button
                            label={mainAddress ? '' : 'Agregar'}
                            icon={mainAddress ? 'pencil-fill' : 'plus'}
                            kind="tertiary"
                            small
                            onClick={onEditMainAddress}
                        />
                    </div>
                )
            }
        },
        {
            name: 'emails',
            type: FieldType.TABLE,
            label: 'Emails',
            render: () => (
                <EmailsTable
                    rows={emailsData}
                    onCreate={onAddEmail}
                    onEditRow={onEditEmail}
                    onRemoveRow={onRemoveEmail}
                />
            )
        },
        {
            name: 'phones',
            type: FieldType.TABLE,
            label: 'Teléfonos',
            render: () => (
                <PhonesTable
                    rows={phonesData}
                    onCreate={onAddPhone}
                    onEditRow={onEditPhone}
                    onRemoveRow={onRemovePhone}
                />
            )
        },
        {
            name: 'deliveryAddresses',
            type: FieldType.TABLE,
            label: 'Direcciones de Entrega',
            render: () => (
                <AddressesTable
                    rows={deliveryAddressesData}
                    showArchived
                    onCreate={onAddDeliveryAddress}
                    onEditRow={onEditDeliveryAddress}
                    onRemoveRow={onRemoveDeliveryAddress}
                />
            )
        },
        {
            name: 'contact',
            type: FieldType.TEXT,
            label: 'Contacto',
            render: () => {
                return (
                    <div className={styles.editableField}>
                        {contact && (
                            <p>{describeContact(contact)}</p>
                        )}
                        <Button
                            label={contact ? '' : 'Agregar'}
                            icon={contact ? 'pencil-fill' : 'plus'}
                            kind="tertiary"
                            small
                            onClick={openContactModal}
                        />
                    </div>
                )
            }
        }
    ]
    if (creditAccount) {
        const creditLimitField = {
            name: 'creditLimit',
            type: FieldType.PRICE,
            label: 'Crédito Otorgado',
            value: creditLimit,
            onChange: onChangeCreditLimit
        }
        const creditDaysField = {
            name: 'creditDays',
            type: FieldType.NUMBER,
            label: 'Días de Crédito',
            render: () => <>
                <NumberField
                    id="creditDays"
                    value={creditDays}
                    integer
                    className={styles.creditDays}
                    onChange={onChangeCreditDays}
                />
                <span>días</span>
            </>
        }
        fields.splice(6, 0, creditLimitField, creditDaysField)
    }

    const validate = (): boolean => {
        const isPersonalId = identifierType === IdentifierType.PERSONAL_ID
        const data = isPersonalId ? { identifier } : { taxId: identifier }
        const validationMessage = isPersonalId ? validateIdentifier(data) : validateTaxId(data)
        validationMessage && setIdentifierValidation(validationMessage)
        return !validationMessage
    }

    const onSubmit = (customerData: Data2) => {
        if (validate()) {
            setModalData(customerData)
            submitCustomerOrOpenModal(
                customerData,
                identifierType!,
                identifier!,
                emailsData,
                phonesData,
                mainAddress,
                deliveryAddressesData,
                contact,
                creditLimit,
                creditDays,
                createMode
            )
        }
    }

    const onCancel = () => navigate(-1)

    const onSaveEmail = (newEmail: string) => {
        const newData = emailToData(newEmail)
        const newEmailsData = currentEmail ?
            emailsData.map(emailData => emailData.email === currentEmail ? newData : emailData) :
            [newData, ...emailsData]
        setEmailsData(newEmailsData)
    }

    const onSavePhone = (newPhone: number) => {
        const newData = phoneToData(newPhone)
        const newPhonesData = currentPhone ?
            phonesData.map(phoneData => phoneData.phone === currentPhone ? newData : phoneData) :
            [newData, ...phonesData]
        setPhonesData(newPhonesData)
    }

    const onSaveDeliveryAddress = (newData: Data2) => {
        const newDeliveryAddressesData = currentDeliveryAddress ?
            deliveryAddressesData.map(deliveryAddressData => deliveryAddressData.id === currentDeliveryAddress.id ? newData : deliveryAddressData) :
            [newData, ...deliveryAddressesData]
        setDeliveryAddressesData(newDeliveryAddressesData)
    }

    const onSaveCustomer = () => {
        submitCustomer(
            modalData!,
            identifierType!,
            identifier!,
            emailsData,
            phonesData,
            mainAddress,
            deliveryAddressesData,
            contact,
            creditLimit,
            creditDays,
            createMode
        )
    }

    return (
        <PanelWrapper title={title} crumbs={crumbs}>
            <DataTypeForm2
               formId="customer-form"
               fields={fields}
               createMode={createMode}
               onSubmit={onSubmit}
               onCancel={onCancel}
            />
            <EmailFormModal
                data={currentEmail ? emailToData(currentEmail) : undefined}
                onSubmit={onSaveEmail}
            />
            <PhoneFormModal
                data={currentPhone ? phoneToData(currentPhone) : undefined}
                onSubmit={onSavePhone}
            />
            <AddressFormModal
                addressData={isMainAddress ? mainAddress : currentDeliveryAddress}
                onSubmit={isMainAddress ? setMainAddress : onSaveDeliveryAddress}
            />
            <ContactFormModal
                contactData={contact}
                onSubmit={setContact}
            />
            <CustomerWarningModal
                createMode={createMode}
                onSubmit={onSaveCustomer}
            />
            <ArchiveModal
                tableName={TableName.ADDRESSES}
                dataType={currentDeliveryAddress as DataType}
                onSubmit={onArchiveDeliveryAddress}
            />
        </PanelWrapper>
    )
}
